import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Default when perms are still loading or the user is anonymous. Empty
// permissions map means the frontend hides sensitive UI until we know
// better; admin bypass is applied via `isAdmin` OR the `is_admin` flag on
// the perms payload.
const EMPTY_PERMS = { is_admin: false, roles: [], overrides: {}, permissions: {} };
const EMPTY_OFFICIAL = { is_admin: false, rbac_role_names: [], permissions: [], branch_scope: null, is_active: true };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=auth
  const [loading, setLoading] = useState(true);
  const [perms, setPerms] = useState(EMPTY_PERMS);
  const [officialPerms, setOfficialPerms] = useState(EMPTY_OFFICIAL);

  const loadPerms = useCallback(async () => {
    // Cargar en paralelo legacy y RBAC oficial. La lectura RBAC oficial es
    // aditiva: si falla o el endpoint no está disponible, dejamos el estado
    // vacío (comportamiento pre-existente intacto).
    const legacyPromise = axios.get(`${API}/me/permissions`).then(r => r.data).catch(() => EMPTY_PERMS);
    const officialPromise = axios.get(`${API}/rbac/me/effective`).then(r => r.data).catch(() => EMPTY_OFFICIAL);
    const [legacy, official] = await Promise.all([legacyPromise, officialPromise]);
    setPerms(legacy || EMPTY_PERMS);
    setOfficialPerms(official || EMPTY_OFFICIAL);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      await loadPerms();
    } catch {
      setUser(false);
      setPerms(EMPTY_PERMS);
      setOfficialPerms(EMPTY_OFFICIAL);
    } finally {
      setLoading(false);
    }
  }, [loadPerms]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
    setUser(data);
    await loadPerms();
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setUser(false);
    setPerms(EMPTY_PERMS);
    setOfficialPerms(EMPTY_OFFICIAL);
  };

  const isAdmin = user && user.kind === "admin";

  /**
   * Permission gate for the UI.
   * • `is_admin` (from any role or the legacy `role=="admin"`) → always true.
   * • Otherwise: check the effective permission map from /api/me/permissions.
   */
  const can = useCallback(
    (module, action) => {
      if (!module || !action) return false;
      if (perms?.is_admin) return true;
      const allowed = (perms?.permissions || {})[module] || [];
      return allowed.includes(action);
    },
    [perms]
  );

  /**
   * RBAC oficial (SECURITY.md) — verifica si el principal actual posee un
   * `permission_id` oficial en su set efectivo. Aditivo al `can()` legacy.
   * • Bypass si el perfil oficial es admin (`is_admin=true`).
   * • También verdadero si el principal es admin legacy (kind='admin').
   */
  const hasOfficialPermission = useCallback(
    (permission_id) => {
      if (!permission_id) return false;
      if (isAdmin) return true;
      if (officialPerms?.is_admin) return true;
      return (officialPerms?.permissions || []).includes(permission_id);
    },
    [officialPerms, isAdmin]
  );

  // Bloque 1 Partner Points — rol oficial "Partner Point" (precios desde
  // backend, sin costos internos en API ni bundle).
  const isPartner = !!(officialPerms?.rbac_role_names || []).includes("Partner Point");

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refresh, isAdmin, perms, can, officialPerms, hasOfficialPermission, isPartner }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
